import React, { useState, useEffect, useRef } from "react";
import styles from "./advance-search-modal.module.css";
import { MyIcons } from "../../icons/my-icons";
import Button from "../../ui/button";
import Input from "../../ui/input";
import Dropdown from "../../ui/dropdown";
import Table from "../../ui/table";
import FormSection from "../../screens/form-section";
import { useNavigate } from "react-router-dom";
import { 
  searchRecords,
  getCompanies,
  getLocalities,
  searchInvoicesAdvanced,
  getStates
} from "../../../services/apiService";

const AdvanceSearchModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState("expediente"); // "expediente" o "factura"

  // Mapeo de nombres de estados a sus valores en la API
  const stateNameToApiValue = (nombre) => {
    const mapping = {
      "Pte Asignar Visita": "PTE_ASIGNAR_VISITA",
      "Pte Parte": "PTE_PARTE",
      "Pte Presupuesto": "PTE_PRESUPUESTO",
      "Pte Asignar Trabajo": "PTE_ASIGNAR_TRABAJO",
      "Pte Aceptación": "PTE_ACEPTACION",
      "Pte Compañia": "PTE_COMPAÑIA",
      "Pte Recordatorio": "PTE_RECORDATORIO",
      "Asignado": "ASIGNADO",
      "Pre Facturado": "PRE_FACTURADO",
      "Pte Facturación": "PTE_FACTURACION",
      "Facturado": "FACTURADO",
      "Cobrado": "COBRADO",
      "Cerrado": "CERRADO",
      "No Cobrable": "NO_COBRABLE",
      "Traspasado": "TRASPASADO"
    };
    return mapping[nombre] || nombre;
  };
  const [formData, setFormData] = useState({
    estadoId: null,
    companiaId: null,
    nombreCliente: "",
    numeroCompania: "",
    domicilioCliente: "",
    localidadClienteId: null,
    nifCliente: "",
    contactoNombre: "",
    contactoTelefono: "",
    // Campos para búsqueda por factura
    numeroFactura: "",
    prefactura: "",
    facturaRectificativa: "",
  });
  const [searchResults, setSearchResults] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [localityOptions, setLocalityOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const modalRef = useRef(null);
  const inputRef = useRef(null);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropdownSelect = (field, option) => {
    setFormData({ ...formData, [field]: option });
  };

  const handleSearchTypeChange = (e) => {
    const newSearchType = e.target.value;
    setSearchType(newSearchType);
    // Limpiar datos previos cuando cambie el tipo de búsqueda
    if (newSearchType === "expediente") {
      setFormData({
        ...formData,
        numeroFactura: "",
        prefactura: "",
        facturaRectificativa: "",
      });
    } else {
      setFormData({
        ...formData,
        estadoId: null,
        companiaId: null,
        nombreCliente: "",
        numeroCompania: "",
        domicilioCliente: "",
        localidadClienteId: null,
        nifCliente: "",
        contactoNombre: "",
        contactoTelefono: "",
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch companies
        const companiesData = await getCompanies();
        const formattedCompanies = companiesData.map(company => ({
          label: company.nombre,
          value: company.id
        }));
        setCompanyOptions(formattedCompanies);

        // Fetch localities
        const localitiesData = await getLocalities();
        const formattedLocalities = localitiesData.map(locality => ({
          label: `${locality.municipio} (${locality.codigoPostal})`,
          value: locality.id
        }));
        setLocalityOptions(formattedLocalities);

        // Fetch states
        const statesData = await getStates();
        const formattedStates = statesData.map(state => ({
          label: state.nombre,
          value: state.id,
          apiValue: stateNameToApiValue(state.nombre) // Valor para enviar a la API
        }));
        setStateOptions(formattedStates);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      // Limpiar el formulario cuando se cierra el modal
      setFormData({
        estadoId: null,
        companiaId: null,
        nombreCliente: "",
        numeroCompania: "",
        domicilioCliente: "",
        localidadClienteId: null,
        nifCliente: "",
        contactoNombre: "",
        contactoTelefono: "",
        numeroFactura: "",
        prefactura: "",
        facturaRectificativa: "",
      });
      setSearchResults([]);
      setHasSearched(false);
      setSearchType("expediente");
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      let results = [];
      
      if (searchType === "expediente") {
        // Búsqueda por expediente (lógica actual)
        const searchParams = {};
        if (formData.estadoId) {
          searchParams.estado = formData.estadoId.apiValue || formData.estadoId.label;
          console.log("[DEBUG] Estado seleccionado:", formData.estadoId);
          console.log("[DEBUG] Valor enviado a la API:", searchParams.estado);
        }
        if (formData.companiaId?.value) searchParams.companiaId = formData.companiaId.value;
        if (formData.nombreCliente.trim()) searchParams.nombreCliente = formData.nombreCliente;
        if (formData.numeroCompania.trim()) searchParams.numeroCompania = formData.numeroCompania;
        if (formData.domicilioCliente.trim()) searchParams.domicilioCliente = formData.domicilioCliente;
        if (formData.localidadClienteId?.value) searchParams.localidadClienteId = formData.localidadClienteId.value;
        if (formData.nifCliente.trim()) searchParams.nifCliente = formData.nifCliente;
        if (formData.contactoNombre.trim()) searchParams.contactoNombre = formData.contactoNombre;
        if (formData.contactoTelefono.trim()) searchParams.contactoTelefono = formData.contactoTelefono;
        
        results = await searchRecords(searchParams);
      } else {
        // Búsqueda por factura - NUEVA FUNCIONALIDAD
        const invoiceSearchParams = {};
        if (formData.numeroFactura.trim()) invoiceSearchParams.numFactura = formData.numeroFactura;
        if (formData.prefactura.trim()) invoiceSearchParams.numPrefactura = formData.prefactura;
        if (formData.facturaRectificativa.trim()) invoiceSearchParams.numFacturaRectificativa = formData.facturaRectificativa;

        results = await searchInvoicesAdvanced(invoiceSearchParams);
      }
      
      setSearchResults(results);
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching records:", error);
      setSearchResults([]);
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Headers para la tabla de resultados
  const headers = [
    { label: "ID", key: "id" },
    { label: "Domicilio", key: "domicilioCliente" },
    { label: "Compañía", key: "compania" },
    { label: "Estado", key: "estado" },
    { label: "Último Operario", key: "ultimoOperario" },
  ];

  const handleRowPress = (row, event) => {
    const recordId = row.expedienteId || row.id;
    if (!recordId) return;

    try {
      const isMiddle = !!(event && (event.nativeEvent?.button === 1 || event.button === 1 || event.which === 2));
      const isModifier = !!(event && (event.ctrlKey || event.metaKey));

      if (isMiddle || isModifier) {
        // Crear un link temporal y simular click para comportamiento nativo del navegador
        const link = document.createElement('a');
        link.href = `/record/${recordId}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        
        // Simular click con las propiedades correctas
        const clickEvent = new MouseEvent('click', {
          button: isMiddle ? 1 : 0,
          ctrlKey: isModifier || event?.ctrlKey,
          metaKey: isModifier || event?.metaKey,
          bubbles: true,
          cancelable: true,
          view: window
        });
        
        link.dispatchEvent(clickEvent);
        document.body.removeChild(link);
      } else {
        // Navigate normally
        navigate(`/record/${recordId}`);
        onClose();
      }
    } catch (err) {
      // Fallback to normal navigation
      navigate(`/record/${recordId}`);
      onClose();
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalRef}>
        <div className={styles.modalHeader}>
          <div className={styles.title}>Búsqueda avanzada</div>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons
              name="close"
              width={24}
              height={24}
              color="var(--dark-grey)"
            />
          </button>
        </div>

        <form onSubmit={handleSearch}>
          <div className={styles.searchTypeContainer}>
            <label htmlFor="searchType" className={styles.searchTypeLabel}>
              Buscar por:
            </label>
            <select 
              id="searchType"
              value={searchType} 
              onChange={handleSearchTypeChange}
              className={styles.searchTypeSelect}
            >
              <option value="expediente">Expediente</option>
              <option value="factura">Factura</option>
            </select>
          </div>

          {searchType === "expediente" ? (
            <>
              <FormSection title="General">
                <div className={styles.sectionRow}>
                  <Dropdown
                    label="Compañía"
                    options={companyOptions}
                    onSelect={(option) => handleDropdownSelect("companiaId", option)}
                    selected={formData.companiaId}
                    mainStyle={{ flex: 1 }}
                    style={{ width: "100%" }}
                  />
                  <Dropdown
                    label="Estado"
                    options={stateOptions}
                    onSelect={(option) => handleDropdownSelect("estadoId", option)}
                    selected={formData.estadoId}
                    mainStyle={{ flex: 1 }}
                    style={{ width: "100%" }}
                  />
                  <Input
                    label="Número de compañía"
                    name="numeroCompania"
                    value={formData.numeroCompania}
                    placeholder="Parámetro de búsqueda"
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  />
                </div>
              </FormSection>

              <FormSection title="Cliente" style={{ marginTop: "12px" }}>
                <div className={styles.sectionRow}>
                  <Input
                    label="Nombre"
                    name="nombreCliente"
                    placeholder="Parámetro de búsqueda"
                    value={formData.nombreCliente}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  />
                  <Input
                    label="Domicilio"
                    name="domicilioCliente"
                    value={formData.domicilioCliente}
                    placeholder="Parámetro de búsqueda"
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  />
                  <Dropdown
                    label="Localidad"
                    options={localityOptions}
                    onSelect={(option) => handleDropdownSelect("localidadClienteId", option)}
                    selected={formData.localidadClienteId}
                    mainStyle={{ flex: 1 }}
                    style={{ width: "100%" }}
                  />
                  <Input
                    label="NIF"
                    name="nifCliente"
                    value={formData.nifCliente}
                    placeholder="Parámetro de búsqueda"
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  />
                </div>
              </FormSection>

              <FormSection title="Contacto" style={{ marginTop: "12px" }}>
                <div className={styles.sectionRow}>
                  <Input
                    label="Nombre contacto"
                    name="contactoNombre"
                    placeholder="Parámetro de búsqueda"
                    value={formData.contactoNombre}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  />
                  <Input
                    label="Teléfono contacto"
                    name="contactoTelefono"
                    value={formData.contactoTelefono}
                    placeholder="Parámetro de búsqueda"
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  />
                </div>
              </FormSection>
            </>
          ) : (
            <FormSection title="Búsqueda por Factura">
              <div className={styles.sectionRow}>
                <Input
                  label="Número Factura"
                  name="numeroFactura"
                  placeholder="Número de factura"
                  value={formData.numeroFactura}
                  onChange={handleInputChange}
                  style={{ flex: 1 }}
                />
                <Input
                  label="Prefactura"
                  name="prefactura"
                  value={formData.prefactura}
                  placeholder="Número de prefactura"
                  onChange={handleInputChange}
                  style={{ flex: 1 }}
                />
                <Input
                  label="Factura Rectificativa"
                  name="facturaRectificativa"
                  value={formData.facturaRectificativa}
                  placeholder="Número de factura rectificativa"
                  onChange={handleInputChange}
                  style={{ flex: 1 }}
                />
              </div>
            </FormSection>
          )}

          <div className={styles.buttonContainer}>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading}>
              {isLoading ? "Buscando..." : "Buscar"}
            </Button>
          </div>
        </form>

        {/* Sección de resultados */}
        {hasSearched && (
          <div style={{ marginTop: "20px" }}>
            <h4 style={{ marginBottom: "16px", color: "var(--black)" }}>
              Resultados de búsqueda ({searchResults.length})
            </h4>
            {searchResults.length > 0 ? (
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <Table headers={headers} data={searchResults} onPress={handleRowPress} />
              </div>
            ) : (
              <div style={{ 
                textAlign: "center", 
                padding: "20px", 
                color: "var(--dark-grey)",
                backgroundColor: "var(--light-grey)",
                borderRadius: "4px"
              }}>
                No se encontraron resultados con los criterios especificados
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvanceSearchModal;
